// Delivery-proof photo — spec §7.
// Bucket: `delivery-proof` (private). Path: `{order_id}/{timestamp}.jpg`.
// Column: `orders.delivery_photo_path`.
//
// The photo is compressed client-side to 1200 px / quality 0.7 BEFORE
// upload — an iPhone will otherwise push 4 MB of raw JPEG.

import { supabase } from './supabase'
import { patchOrder, patchPickupGroup } from './admin'

const MAX_WIDTH = 1200
const QUALITY = 0.7
const BUCKET = 'delivery-proof'

/**
 * Compress an image file to a max-width JPEG blob.
 * @param {File | Blob} file
 * @returns {Promise<Blob>}
 */
export async function compressImage(file) {
  // createImageBitmap handles EXIF orientation on modern browsers.
  const bitmap = await createImageBitmap(file)
  const scale = Math.min(1, MAX_WIDTH / bitmap.width)
  const w = Math.round(bitmap.width * scale)
  const h = Math.round(bitmap.height * scale)
  const canvas = document.createElement('canvas')
  canvas.width = w
  canvas.height = h
  const ctx = canvas.getContext('2d')
  ctx.drawImage(bitmap, 0, 0, w, h)
  bitmap.close?.()
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('toBlob returned null'))),
      'image/jpeg',
      QUALITY,
    )
  })
}

/**
 * Upload a delivery-proof photo. Compresses first, then stores at
 * `{order_id}/{timestamp}.jpg` and writes the path onto the order.
 * @param {string} orderId
 * @param {File | Blob} file
 * @returns {Promise<string>} the storage path
 */
export async function uploadDeliveryProof(orderId, file) {
  if (!orderId) throw new Error('uploadDeliveryProof: missing orderId')
  const blob = await compressImage(file)
  const path = `${orderId}/${Date.now()}.jpg`
  const { error } = await supabase.storage.from(BUCKET).upload(path, blob, {
    contentType: 'image/jpeg',
    upsert: true,
  })
  if (error) throw error
  await patchOrder(orderId, { delivery_photo_path: path })
  return path
}

/**
 * Same photo for a whole multi-service pickup: the bags were handed over in
 * one trip, so there is one proof, written onto every order of the group by
 * a single UPDATE — no pickup can end up half-photographed.
 *
 * The file stays under `{head_order_id}/…` on purpose: the customer-facing
 * storage policy (`delivery_proof_owner_read`) matches the FIRST path segment
 * against the ids of that customer's orders. A group-id folder would match
 * nothing and the customer could never open their own photo.
 *
 * @param {string} headOrderId first order of the pickup
 * @param {string} groupId     orders.pickup_group_id
 * @param {File | Blob} file
 * @returns {Promise<string>} the storage path
 */
export async function uploadDeliveryProofForPickup(headOrderId, groupId, file) {
  if (!headOrderId) throw new Error('uploadDeliveryProofForPickup: missing headOrderId')
  if (!groupId) throw new Error('uploadDeliveryProofForPickup: missing groupId')
  const blob = await compressImage(file)
  const path = `${headOrderId}/${Date.now()}.jpg`
  const { error } = await supabase.storage.from(BUCKET).upload(path, blob, {
    contentType: 'image/jpeg',
    upsert: true,
  })
  if (error) throw error
  await patchPickupGroup(groupId, { delivery_photo_path: path })
  return path
}

/**
 * Ephemeral signed URL for display in the admin UI. 5 min lifetime is
 * enough for a card thumbnail or a preview click. The 7-day URL that
 * ships in the email is generated server-side by the Edge Function.
 * @param {string | null | undefined} path
 * @param {number} [expiresIn]
 * @returns {Promise<string | null>}
 */
export async function getDeliveryProofUrl(path, expiresIn = 300) {
  if (!path) return null
  const { data, error } = await supabase.storage
    .from(BUCKET)
    .createSignedUrl(path, expiresIn)
  if (error) {
    // eslint-disable-next-line no-console
    console.warn('[deliveryPhoto] signed URL failed', error)
    return null
  }
  return data?.signedUrl || null
}
